import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import deepEqual from 'fast-deep-equal'
import type {Ref} from 'react'
import {
  cloneElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {emptyArray} from '../../../utils/empty'

import {CheckModel} from './Tree.consts'
import type {Node, NodeValue} from './Tree.types'
import type {FlatNode} from './TreeModel'
import TreeModel from './TreeModel'
import TreeNode from './TreeNode/TreeNode'

import styles from './Tree.css'

type Props = {
  labelId: string
  className?: string
  id?: string
  nodes: ReadonlyArray<Node>
  checkModel?: CheckModel
  checked?: ReadonlyArray<NodeValue>
  expanded?: ReadonlyArray<NodeValue>
  checkable?: boolean
  expandOnClick?: boolean
  optimisticToggle?: boolean
  expandOnlyOneLeaf?: boolean
  checkAllButtonName?: string
  onCheck?: (arg0: ReadonlyArray<string>, node?: NodeValue, expanded?: boolean) => unknown
  onExpand?: (arg0: ReadonlyArray<string>, node?: NodeValue, expanded?: boolean) => unknown
  focusOnArrow?: boolean
}

export type Handle = {
  onExpandAll: () => void
  onCollapseAll: () => void
}

function Tree(props: Props, ref: Ref<Handle>) {
  const {
    className,
    id = 'TreeId',
    nodes,
    checked = emptyArray,
    expanded = emptyArray,
    checkable = true,
    optimisticToggle = true,
    expandOnlyOneLeaf,
    checkModel = CheckModel.LEAF,
    checkAllButtonName = 'Select all',
    labelId,
    focusOnArrow,
    onCheck: onCheckExternal,
    onExpand: onExpandExternal,
  } = props
  const [model, setModel] = useState(() => new TreeModel({}, expandOnlyOneLeaf))
  const prevProps = useRef<Props>()
  useLayoutEffect(() => {
    // Since flattening nodes is an expensive task, only update when there is a node change
    if (!deepEqual(prevProps.current?.nodes, nodes)) {
      setModel(prevModel => {
        const clonedModel = prevModel.clone()
        clonedModel.flattenNodes(nodes)
        clonedModel.deserializeLists({
          checked,
          expanded,
        })
        return clonedModel
      })
    }
    prevProps.current = props
  }, [nodes, checked, expanded, props])

  useLayoutEffect(() => {
    setModel(prevModel => {
      const clonedModel = prevModel.clone()
      clonedModel.deserializeLists({
        checked,
        expanded,
      })
      return clonedModel
    })
  }, [checked, expanded])
  const onCheck = useCallback(
    (value: NodeValue, checkedValue: boolean) => {
      setModel(prevModel => {
        const clonedModel = prevModel.clone()
        const node = clonedModel.getNode(value)
        clonedModel.toggleChecked(node, checkedValue, checkModel)

        if (onCheckExternal != null) {
          onCheckExternal(clonedModel.serializeList('checked'), value, checkedValue)
        }
        return clonedModel
      })
    },
    [checkModel, onCheckExternal],
  )
  const onExpand = useCallback(
    (value: NodeValue, expandedValue: boolean) => {
      setModel(prevModel => {
        const clonedModel = prevModel.clone()
        clonedModel.expandNode(value, expandedValue)

        if (onExpandExternal != null) {
          onExpandExternal(clonedModel.serializeList('expanded'), value, expandedValue)
        }
        return clonedModel
      })
    },
    [onExpandExternal],
  )
  const expandAllNodes = useCallback(
    (expand = true) => {
      setModel(prevModel => {
        const clonedModel = prevModel.clone()
        clonedModel.expandAllNodes(expand)
        if (onExpandExternal != null) {
          onExpandExternal(clonedModel.serializeList('expanded'))
        }
        return clonedModel
      })
    },
    [onExpandExternal],
  )
  const checkAllNodes = useCallback(
    (isChecked = true) => {
      setModel(prevModel => {
        const clonedModel = prevModel.clone()
        clonedModel.checkAllNodes(isChecked, checkModel)
        if (onCheckExternal != null) {
          onCheckExternal(clonedModel.serializeList('checked'))
        }
        return clonedModel
      })
    },
    [checkModel, onCheckExternal],
  )
  useImperativeHandle(ref, () => ({
    onExpandAll: () => expandAllNodes(),
    onCollapseAll: () => expandAllNodes(false),
  }))
  const isEveryChildChecked = useCallback(
    (node: Node) =>
      node.children != null
        ? node.children.every(child => {
            const {checkState} = model.getNode(child.value) ?? {}
            return checkState != null ? checkState === 1 : false
          })
        : false,
    [model],
  )
  const isSomeChildChecked = useCallback(
    (node: Node) =>
      node.children != null
        ? node.children.some(child => {
            const {checkState} = model.getNode(child.value) ?? {}
            return checkState != null ? checkState > 0 : false
          })
        : true,
    [model],
  )
  const determineShallowCheckState = useCallback(
    (node: Node) => {
      const flatNode: Partial<FlatNode> = model.getNode(node.value) ?? {}

      if (flatNode.isLeaf) {
        return flatNode.checked === true ? 1 : 0
      }

      if (isEveryChildChecked(node)) {
        return 1
      }

      if (isSomeChildChecked(node)) {
        return 2
      }

      return 0
    },
    [isEveryChildChecked, isSomeChildChecked, model],
  )
  const checkedAll = model
    .clone()
    .serializeList(checkModel === CheckModel.LEAF ? 'isLeaf' : 'value')
    .every(nodeId => checked.includes(nodeId))
  const onCheckAll = useCallback(() => checkAllNodes(), [checkAllNodes])
  const onUncheckAll = useCallback(() => checkAllNodes(false), [checkAllNodes])
  const renderCheckAllButton = useMemo(() => {
    const count = checked.length
    return (
      <div className={styles.checkAll} role="treeitem" aria-selected={checkedAll}>
        <Checkbox
          containerClassName={styles.checkAllCheckbox}
          indeterminate={count > 0 && !checkedAll}
          checked={checkedAll}
          onChange={checkedAll ? onUncheckAll : onCheckAll}
        >
          {checkAllButtonName}
        </Checkbox>
        {count > 0 && <span className={styles.checkAllCount}>{`${count} selected`}</span>}
      </div>
    )
  }, [checked.length, checkedAll, onUncheckAll, onCheckAll, checkAllButtonName])
  const renderTreeNodes = (nodesLocal: ReadonlyArray<Node>, parent?: Node, level = 0) => {
    if (nodesLocal == null) {
      return null
    }

    const treeNodes = nodesLocal.map(node => {
      const flatNode: Partial<FlatNode> = model.getNode(node.value) ?? {}
      const children =
        flatNode.isParent === true && node.children != null
          ? renderTreeNodes(node.children, node, level + 1)
          : null
      // Determine the check state after all children check states have been determined
      // This is done during rendering as to avoid an additional loop during the
      // deserialization of the `checked` property
      flatNode.checkState = determineShallowCheckState(node)
      // Render only if parent is expanded or if there is no root parent
      const parentExpanded = parent?.value ? model.getNode(parent.value)?.expanded === true : true
      return parentExpanded ? (
        <TreeNode
          key={node.value}
          checkable={checkable}
          checked={flatNode.checkState}
          className={node.className}
          labelClassName={node.labelClassName}
          expandable={node.expandable}
          expanded={flatNode.expanded === true}
          label={node.label}
          component={node.component}
          detailsComponent={node.detailsComponent}
          optimisticToggle={optimisticToggle}
          isLeaf={flatNode.isLeaf === true}
          treeId={id}
          value={node.value}
          level={level}
          focusOnArrow={focusOnArrow}
          onCheck={onCheck}
          onExpand={onExpand}
        >
          {children}
        </TreeNode>
      ) : null
    })
    return (
      <>
        <div className={styles.nodes} role="group">
          {parent?.childrenComponent != null
            ? cloneElement(parent?.childrenComponent, {children: treeNodes, level})
            : treeNodes}
        </div>
        {parent?.footerComponent != null
          ? cloneElement(parent.footerComponent, {
              level,
            })
          : null}
      </>
    )
  }
  return (
    <div
      className={classNames(className, styles.tree)}
      id={id}
      role="tree"
      aria-multiselectable={checkable}
      aria-labelledby={labelId}
    >
      {checkable ? renderCheckAllButton : null}
      {renderTreeNodes(nodes)}
    </div>
  )
}

export default forwardRef(Tree)
